// packages/web-ui/src/AppShell/ContextPanel-modes/KbMode.tsx
//
// Story W2-S3 (initial scaffold) + Story W4-S5 (this commit) — populated KB
// Browser ContextPanel body.
//
// W4-S5 wires the W2-S3 stub to the real `kb` zustand slice
// (`packages/web-ui/src/state/kb.ts`):
//   - search input (debounced 200 ms) drives `kb.setSearch` + refetch
//   - filter chips (Parts / Ports / IF / Conn) drive `kb.setSelectedKinds`
//   - tree body renders `<KbEntityTree>` from the slice's `entities`
//   - status pulls from the slice rather than the W2-S3 stub helper
//
// The legacy `data-testid="kb-mode-root"` is preserved (W2-S3 contract /
// e2e tests). The W2-S3 ModeChrome wrapper is bypassed here so the tree can
// own the body region directly — but the chrome's testid naming convention
// (header / filters / search / body / empty / error) is replicated verbatim.

import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import type { KbEntityKind } from '@tessa-lean/base/api-schemas/index.js';
import { KbEntityTree } from '../../components/kb-browser/index.js';
import type { KbTreeGroupMode } from '../../components/kb-browser/KbEntityTree.js';
import { useOpenResource } from '../../hooks/useOpenResource.js';
import { createKbStore, type KbSliceConfig } from '../../state/kb.js';

const FILTER_CHIPS: ReadonlyArray<{ label: string; testId: string; kind: KbEntityKind }> = [
  { label: 'Parts', testId: 'kb-chip-parts', kind: 'part' },
  { label: 'Ports', testId: 'kb-chip-ports', kind: 'port' },
  { label: 'IF', testId: 'kb-chip-if', kind: 'interface' },
  { label: 'Conn', testId: 'kb-chip-conn', kind: 'connection' },
];

const SEARCH_DEBOUNCE_MS = 200;

/**
 * Singleton kb store — instantiated lazily on first `<KbMode>` render. Tests
 * pass a config override via the `_storeOverride` prop (escape hatch — kept
 * undocumented because the production path is the singleton).
 */
let _kbStore: ReturnType<typeof createKbStore> | null = null;
function getKbStore(config?: KbSliceConfig): ReturnType<typeof createKbStore> {
  if (config !== undefined) return createKbStore(config);
  _kbStore ??= createKbStore();
  return _kbStore;
}

/** Test-only: reset the lazy singleton so each test starts fresh. */
export function _resetKbModeStoreForTests(): void {
  _kbStore = null;
}

export interface KbModeProps {
  /** Test escape-hatch — supply a fresh store config (avoids singleton leakage). */
  _storeOverride?: KbSliceConfig;
}

export function KbMode({ _storeOverride }: KbModeProps = {}): JSX.Element {
  const store = useMemo(() => getKbStore(_storeOverride), [_storeOverride]);
  const state = useStore(store);

  // Local input state (the raw keystroke value); the debounced value writes
  // into the slice's `search` field.
  const [inputValue, setInputValue] = useState<string>(state.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tree grouping mode — purely local UI state; defaults to "By level" so the
  // user sees the canonical KB layering on first open.
  const [groupMode, setGroupMode] = useState<KbTreeGroupMode>('level');
  const openResource = useOpenResource();

  // Refetch on mount AND when search/selectedKinds change. The previous code
  // ran two separate effects (one mount-only, one keyed on the same store)
  // which fired four `/api/v1/kb/entities` calls on initial open under
  // React 19 StrictMode double-invoke. A single keyed effect plus
  // `refetchEntities`'s AbortController cancellation is enough.
  const refetchKey = `${state.search}|${state.selectedKinds.join(',')}`;
  useEffect(() => {
    void store.getState().refetchEntities();
  }, [refetchKey, store]);

  // Drain the search debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, []);

  const onSearchChange = (value: string): void => {
    setInputValue(value);
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      store.getState().setSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setInputValue('');
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      store.getState().setSearch('');
    }
  };

  const toggleKind = (kind: KbEntityKind): void => {
    const current = new Set(state.selectedKinds);
    if (current.has(kind)) current.delete(kind);
    else current.add(kind);
    store.getState().setSelectedKinds(Array.from(current));
  };

  // Migration final fix-pass — empty-state should fire whenever the slice
  // settles with no entities, regardless of whether the post-refetch status
  // landed in 'idle' or 'ready'. The W4-S5 mount fires `refetchEntities()`
  // synchronously from `useEffect` which immediately flips status from
  // 'idle' to 'loading'; on a successful empty-list response status lands
  // at 'ready' and (pre-fix) `showEmpty` no longer matched, so the user
  // saw a blank "ready" body instead of the empty-state copy.
  const noEntities = state.entities.length === 0;
  const showEmpty = noEntities && (state.status === 'idle' || state.status === 'ready');
  const showLoading = state.status === 'loading';
  const showError = state.status === 'error';
  const showReady = !noEntities && (state.status === 'ready' || state.status === 'idle');

  return (
    <div
      data-testid="kb-mode-root"
      data-status={state.status}
      className="flex h-full flex-col bg-cream-100"
    >
      <header
        data-testid="kb-mode-root-header"
        className="flex h-8 items-center justify-between border-b border-taupe-300 px-2 text-sm font-semibold"
      >
        <span>KB Browser</span>
      </header>

      <div
        data-testid="kb-mode-root-filters"
        className="flex h-8 items-center gap-1 border-b border-taupe-300 px-2 text-xs"
      >
        <div data-testid="kb-mode-root-filter-chips" className="flex items-center gap-1">
          {FILTER_CHIPS.map((chip) => {
            const selected = state.selectedKinds.includes(chip.kind);
            return (
              <button
                key={chip.kind}
                type="button"
                role="checkbox"
                aria-checked={selected}
                data-testid={chip.testId}
                onClick={() => {
                  toggleKind(chip.kind);
                }}
                className={[
                  'rounded border px-2 py-0.5 text-[10px]',
                  selected
                    ? 'border-taupe-700 bg-[var(--accent-cream-active)]'
                    : 'border-taupe-300 bg-cream-50',
                ].join(' ')}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        <div
          data-testid="kb-mode-root-group-toggle"
          role="radiogroup"
          aria-label="Tree grouping mode"
          className="ml-auto flex items-center gap-1"
        >
          <button
            type="button"
            role="radio"
            aria-checked={groupMode === 'level'}
            data-testid="kb-mode-root-group-level"
            onClick={() => {
              setGroupMode('level');
            }}
            className={[
              'rounded border px-2 py-0.5 text-[10px]',
              groupMode === 'level'
                ? 'border-taupe-700 bg-[var(--accent-cream-active)]'
                : 'border-taupe-300 bg-cream-50',
            ].join(' ')}
          >
            By level
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={groupMode === 'parent'}
            data-testid="kb-mode-root-group-parent"
            onClick={() => {
              setGroupMode('parent');
            }}
            className={[
              'rounded border px-2 py-0.5 text-[10px]',
              groupMode === 'parent'
                ? 'border-taupe-700 bg-[var(--accent-cream-active)]'
                : 'border-taupe-300 bg-cream-50',
            ].join(' ')}
          >
            By parent
          </button>
        </div>
      </div>

      <div className="sticky top-0 z-[1] border-b border-taupe-300 bg-cream-100 p-1">
        <input
          type="text"
          data-testid="kb-mode-root-search"
          aria-label="Search KB entities"
          placeholder="Search KB entities…"
          value={inputValue}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
          onKeyDown={onSearchKeyDown}
          className="h-7 w-full rounded border border-taupe-300 bg-cream-50 px-2 text-xs outline-none focus:border-taupe-700"
        />
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="kb-mode-root-body">
        {showLoading ? (
          <ul
            data-testid="kb-mode-root-skeleton"
            className="flex flex-col gap-2 p-3"
          >
            <li className="h-4 rounded bg-cream-200 animate-pulse" />
            <li className="h-4 rounded bg-cream-200 animate-pulse" />
            <li className="h-4 rounded bg-cream-200 animate-pulse" />
          </ul>
        ) : null}
        {showEmpty ? (
          <div
            data-testid="kb-mode-root-empty"
            className="flex h-full flex-col items-center justify-center px-4 text-center text-xs text-taupe-700"
          >
            <p>Knowledge base is empty. Initialise via /kb-init or open in chat.</p>
          </div>
        ) : null}
        {showError ? (
          <div
            data-testid="kb-mode-root-error"
            className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-xs"
          >
            <p>{state.errorMessage ?? 'Could not load KB index.'}</p>
            <button
              type="button"
              data-testid="kb-mode-root-retry"
              className="rounded border border-taupe-500 bg-cream-200 px-3 py-1 text-xs"
              onClick={() => {
                void store.getState().refetchEntities();
              }}
            >
              Retry
            </button>
          </div>
        ) : null}
        {showReady ? (
          <KbEntityTree
            entities={state.entities}
            query={state.search}
            selectedEntityId={state.selectedEntityId}
            recentlyChangedEntityIds={state.recentlyChangedEntityIds}
            groupMode={groupMode}
            onSelectEntity={(entityId) => {
              store.getState().selectEntity(entityId);
              // Open (or activate) the matching kb tab so the centre pane
              // shows the entity's IBD + detail card.
              openResource('kb', entityId);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
